'use client';

import { c as _c } from "react/compiler-runtime";
import { useWallets } from '@morfi/backend-sdk-v2/client';
import { formatFiatUnits, fundingSettlementValue, OnrampApiError, type OnrampClient, withdrawalFeeEstimate } from '@morfi/onramp-sdk';
import { useFundingCurrency, useWithdrawalAmount, useWithdrawalCheckout } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import { type ChangeEvent, type ReactNode, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InputField } from '@/components/ui/input-field';
import { getUsdcMint } from '@/config/solana';
import { useTransferUsdc } from '@/hooks/wallet/use-transfer-usdc';
import { getActiveNetwork } from '@/lib/network/active';
import { logger } from '@/lib/telemetry/logger';
import { isWithdrawalRecipient } from '@/lib/wallet/withdraw-recipient';
import { useExchangeRates } from '@/providers/exchange-rate-provider';
import { useCurrencyStore } from '@/stores/currency';
import { FundingFees } from '../checkout/amount/funding-fees';
import { FundingHistory } from '../history/funding-history';
import { FundingChromeContext, useFundingChrome } from '../shared/funding-chrome';
import { FundingDialog } from '../shared/funding-dialog';
import { FundingError } from '../shared/funding-error';
import { FundingLoading } from '../shared/funding-loading';
import { useFundingRealtime } from '../shared/funding-realtime';
import { WithdrawAmountScreen } from './withdraw-amount-screen';
import { WithdrawMethodPicker } from './withdraw-method-picker';
import { WithdrawOperationScreen } from './withdraw-operation-screen';
export function WithdrawCheckout(t0) {
  const $ = _c(130);
  const {
    client,
    open,
    onClose,
    onProfile
  } = t0;
  const t = useTranslations("onramp");
  const locale = useLocale();
  const selectedCurrency = useCurrencyStore(_temp);
  const realtime = useFundingRealtime(client, open);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = crypto.randomUUID.bind(crypto);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const flow = useWithdrawalCheckout(client, "web", t1, open, selectedCurrency, locale, realtime);
  const {
    wallets
  } = useWallets();
  const transfer = useTransferUsdc();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [value, setValue] = useState("");
  const [inputUnits, setInputUnits] = useState(null);
  let t2;
  if ($[1] !== t) {
    t2 = t("withdraw_title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const chrome = useFundingChrome(t2);
  const context = flow.context.data;
  const rates = useExchangeRates();
  const currency = useFundingCurrency(flow.currency, rates.fundingFx);
  const amount = useWithdrawalAmount(client, flow.method, "web", currency, inputUnits, context?.balanceD6 ?? "0", open && flow.screen === "amount", context);
  let reviewUnits;
  let t3;
  if ($[3] !== amount || $[4] !== currency.currency || $[5] !== currency.decimals || $[6] !== currency.fx || $[7] !== currency.status || $[8] !== flow.amount || $[9] !== flow.preview || $[10] !== flow.screen) {
    reviewUnits = currency.status === "ready" && currency.fx ? fundingSettlementValue(flow.amount || "0", currency.currency, currency.decimals, currency.fx) : null;
    t3 = flow.screen === "amount" ? amount.estimate : withdrawalFeeEstimate(flow.preview, flow.amount, reviewUnits, currency.currency, currency.decimals);
    $[3] = amount;
    $[4] = currency.currency;
    $[5] = currency.decimals;
    $[6] = currency.fx;
    $[7] = currency.status;
    $[8] = flow.amount;
    $[9] = flow.preview;
    $[10] = flow.screen;
    $[11] = reviewUnits;
    $[12] = t3;
  } else {
    reviewUnits = $[11];
    t3 = $[12];
  }
  const feeEstimate = t3;
  const operation = flow.operation.data;
  const requestError = (flow.context.isError ? flow.context.error : null) ?? (flow.activeId && flow.operation.isError ? flow.operation.error : null) ?? flow.resumeError ?? (flow.commands.isError ? flow.commands.error : null) ?? (flow.screen === "amount" && amount.quote.isError ? amount.quote.error : null);
  const expired = requestError instanceof OnrampApiError && requestError.status === 401;
  const actionLabel = expired ? "sign_in" : flow.commands.isError ? "retry" : "continue";
  let t4;
  if ($[13] !== expired || $[14] !== flow || $[15] !== onClose) {
    t4 = function retryRequest() {
      if (expired) {
        onClose();
      } else {
        if (flow.activeId) {
          flow.retryOperation();
        } else {
          flow.context.refetch();
        }
      }
    };
    $[13] = expired;
    $[14] = flow;
    $[15] = onClose;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const retryRequest = t4;
  let t5;
  if ($[17] !== flow.recipient || $[18] !== wallets) {
    t5 = isWithdrawalRecipient(flow.recipient, wallets.map(_temp2));
    $[17] = flow.recipient;
    $[18] = wallets;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const validRecipient = t5;
  let t6;
  if ($[20] !== expired || $[21] !== flow.commands || $[22] !== flow.screen || $[23] !== onClose) {
    t6 = function run(command) {
      if (expired) {
        onClose();
        return;
      }
      flow.commands.mutateAsync(command).catch(() => logger.warn("offramp.command_failed", {
        stage: flow.screen
      }));
    };
    $[20] = expired;
    $[21] = flow.commands;
    $[22] = flow.screen;
    $[23] = onClose;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const run = t6;
  let t7;
  if ($[25] !== transfer || $[26] !== wallets) {
    t7 = async function send(source, recipient, amountD6, network, operationId, mint, beforeBroadcast) {
      if ((getActiveNetwork() === "mainnet" ? "mainnet" : "devnet") !== network || getUsdcMint(network) !== mint) {
        throw new Error("offramp_network_changed");
      }
      const wallet_0 = wallets.find(entry => entry.address === source && entry.archivedAt === null);
      if (!wallet_0) {
        throw new Error("offramp_wallet_unavailable");
      }
      const signature = await transfer.submit(wallet_0.id, source, wallet_0.type, recipient, BigInt(amountD6), operationId, network, beforeBroadcast);
      if (!signature) {
        throw new Error("offramp_submission_unknown");
      }
      return signature;
    };
    $[25] = transfer;
    $[26] = wallets;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const send = t7;
  const t8 = context?.decimals ?? 2;
  const t9 = flow.screen === "amount" ? inputUnits : reviewUnits;
  const t10 = flow.screen === "amount" ? inputUnits : reviewUnits;
  const t11 = flow.screen === "amount" && amount.loading;
  let t12;
  if ($[28] !== feeEstimate || $[29] !== flow.currency || $[30] !== t10 || $[31] !== t11 || $[32] !== t8 || $[33] !== t9) {
    t12 = <FundingFees currency={flow.currency} decimals={t8} principalUnits={t9} estimate={feeEstimate} direction="withdraw" walletDebitUnits={t10} loading={t11} />;
    $[28] = feeEstimate;
    $[29] = flow.currency;
    $[30] = t10;
    $[31] = t11;
    $[32] = t8;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== flow.method || $[36] !== t) {
    t13 = flow.method === "manual_withdraw" ? <p className="text-caption text-foreground-secondary">{t("withdraw_network_fee")}</p> : null;
    $[35] = flow.method;
    $[36] = t;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t12 || $[39] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const fees = t14;
  const t15 = open && !historyOpen;
  let t16;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setHistoryOpen(true);
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const t17 = flow.screen === "payouts";
  const t18 = !flow.activeId && flow.screen !== "payouts";
  let t19;
  if ($[42] !== chrome || $[43] !== flow.back || $[44] !== onProfile || $[45] !== t17 || $[46] !== t18) {
    t19 = {
      ...chrome,
      hasProfile: true,
      openProfile: onProfile,
      picker: t17,
      hasBack: t18,
      back: flow.back
    };
    $[42] = chrome;
    $[43] = flow.back;
    $[44] = onProfile;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== flow.activeId || $[49] !== flow.context.isPending || $[50] !== flow.operation.isPending || $[51] !== flow.screen) {
    t20 = flow.context.isPending || flow.activeId && flow.operation.isPending ? <FundingLoading withdrawal={flow.screen === "payouts" && !flow.activeId} /> : null;
    $[48] = flow.activeId;
    $[49] = flow.context.isPending;
    $[50] = flow.operation.isPending;
    $[51] = flow.screen;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== onClose || $[54] !== requestError || $[55] !== retryRequest) {
    t21 = requestError ? <FundingError withdrawal={true} error={requestError} onSignIn={onClose} onRetry={retryRequest} /> : null;
    $[53] = onClose;
    $[54] = requestError;
    $[55] = retryRequest;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== context || $[58] !== expired || $[59] !== flow.activeId || $[60] !== flow.context.isError || $[61] !== flow.context.isFetching || $[62] !== flow.operation.isError || $[63] !== flow.operation.isFetching || $[64] !== operation || $[65] !== retryRequest || $[66] !== t) {
    t22 = flow.context.isError && !context || flow.activeId && flow.operation.isError && !operation ? <AnimatedButton loading={flow.context.isFetching || flow.operation.isFetching} onClick={retryRequest}>{t(expired ? "sign_in" : "retry")}</AnimatedButton> : null;
    $[57] = context;
    $[58] = expired;
    $[59] = flow.activeId;
    $[60] = flow.context.isError;
    $[61] = flow.context.isFetching;
    $[62] = flow.operation.isError;
    $[63] = flow.operation.isFetching;
    $[64] = operation;
    $[65] = retryRequest;
    $[66] = t;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== context || $[69] !== flow.screen || $[70] !== flow.select) {
    t23 = flow.screen === "payouts" && context ? <WithdrawMethodPicker methods={context.methods} onSelect={flow.select} /> : null;
    $[68] = context;
    $[69] = flow.screen;
    $[70] = flow.select;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  let t24;
  if ($[72] !== flow || $[73] !== t || $[74] !== validRecipient) {
    t24 = flow.screen === "recipient" ? <><InputField label={t("recipient_wallet")} value={flow.recipient} onChange={event => flow.setRecipient(event.target.value.trim())} autoComplete="off" /><AnimatedButton disabled={!validRecipient} onClick={flow.recipientReady}>{t("continue")}</AnimatedButton></> : null;
    $[72] = flow;
    $[73] = t;
    $[74] = validRecipient;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  let t25;
  if ($[76] !== amount || $[77] !== context || $[78] !== currency || $[79] !== expired || $[80] !== fees || $[81] !== flow || $[82] !== retryRequest || $[83] !== run || $[84] !== value) {
    t25 = flow.screen === "amount" && context ? <WithdrawAmountScreen flow={flow} amount={amount} currency={currency} context={context} value={value} setValue={setValue} setInputUnits={setInputUnits} expired={expired} retryRequest={retryRequest} run={run} fees={fees} /> : null;
    $[76] = amount;
    $[77] = context;
    $[78] = currency;
    $[79] = expired;
    $[80] = fees;
    $[81] = flow;
    $[82] = retryRequest;
    $[83] = run;
    $[84] = value;
    $[85] = t25;
  } else {
    t25 = $[85];
  }
  let t26;
  if ($[86] !== actionLabel || $[87] !== context?.source || $[88] !== currency.currency || $[89] !== currency.decimals || $[90] !== fees || $[91] !== flow.commands || $[92] !== flow.method || $[93] !== flow.recipient || $[94] !== flow.screen || $[95] !== locale || $[96] !== reviewUnits || $[97] !== run || $[98] !== t) {
    t26 = flow.screen === "review" ? <><h3 className="text-heading-md">{t("withdraw_review")}</h3><p className="text-heading-lg">{reviewUnits === null ? t("fees_pending") : formatFiatUnits(reviewUnits, currency.decimals, currency.currency, locale)}</p><p className="break-all text-caption">{flow.method === "manual_withdraw" ? flow.recipient : t("payout_account_subtitle")}</p><p className="break-all text-caption">{t("withdraw_source")}: {context?.source}</p>{fees}<AnimatedButton loading={flow.commands.isPending} disabled={flow.commands.isPending} onClick={() => run({
        kind: "create"
      })}>{t(actionLabel)}</AnimatedButton></> : null;
    $[86] = actionLabel;
    $[87] = context?.source;
    $[88] = currency.currency;
    $[89] = currency.decimals;
    $[90] = fees;
    $[91] = flow.commands;
    $[92] = flow.method;
    $[93] = flow.recipient;
    $[94] = flow.screen;
    $[95] = locale;
    $[96] = reviewUnits;
    $[97] = run;
    $[98] = t;
    $[99] = t26;
  } else {
    t26 = $[99];
  }
  let t27;
  if ($[100] !== expired || $[101] !== flow || $[102] !== onClose || $[103] !== retryRequest || $[104] !== run || $[105] !== send) {
    t27 = <WithdrawOperationScreen flow={flow} expired={expired} retryRequest={retryRequest} run={run} send={send} onClose={onClose} />;
    $[100] = expired;
    $[101] = flow;
    $[102] = onClose;
    $[103] = retryRequest;
    $[104] = run;
    $[105] = send;
    $[106] = t27;
  } else {
    t27 = $[106];
  }
  let t28;
  if ($[107] !== t20 || $[108] !== t21 || $[109] !== t22 || $[110] !== t23 || $[111] !== t24 || $[112] !== t25 || $[113] !== t26 || $[114] !== t27) {
    t28 = <div className="rr-block flex flex-col gap-4">{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}</div>;
    $[107] = t20;
    $[108] = t21;
    $[109] = t22;
    $[110] = t23;
    $[111] = t24;
    $[112] = t25;
    $[113] = t26;
    $[114] = t27;
    $[115] = t28;
  } else {
    t28 = $[115];
  }
  let t29;
  if ($[116] !== onClose || $[117] !== t15 || $[118] !== t19 || $[119] !== t28) {
    t29 = <FundingDialog open={t15} onClose={onClose} onHistory={t16} chrome={t19}>{t28}</FundingDialog>;
    $[116] = onClose;
    $[117] = t15;
    $[118] = t19;
    $[119] = t28;
    $[120] = t29;
  } else {
    t29 = $[120];
  }
  const t30 = open && historyOpen;
  let t31;
  if ($[121] !== client || $[122] !== onClose || $[123] !== realtime || $[124] !== t30) {
    t31 = <FundingHistory realtime={realtime} origin="withdraw" client={client} open={t30} onOpenChange={setHistoryOpen} onSignIn={onClose} />;
    $[121] = client;
    $[122] = onClose;
    $[123] = realtime;
    $[124] = t30;
    $[125] = t31;
  } else {
    t31 = $[125];
  }
  let t32;
  if ($[126] !== chrome.report || $[127] !== t29 || $[128] !== t31) {
    t32 = <FundingChromeContext.Provider value={chrome.report}>{t29}{t31}</FundingChromeContext.Provider>;
    $[126] = chrome.report;
    $[127] = t29;
    $[128] = t31;
    $[129] = t32;
  } else {
    t32 = $[129];
  }
  return t32;
}
function _temp2(wallet) {
  return wallet.address;
}
function _temp(state) {
  return state.currency;
}